/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice Homework page — the taking experience for tutor-assigned practices
 * (revealPerQuestion). Unlike the standard Challenge session (review at the end), the student
 * answers a question, submits it, and immediately sees the correct answer + rationale for THAT
 * question before moving on — the common tutoring/homework flow. Responses are recorded through the
 * same per-question response endpoint, so results + the tutor's review work unchanged.
 *
 * This software is proprietary and confidential. Unauthorized copying, distribution, or
 * modification is strictly prohibited.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Target, ChevronRight, CheckCircle2, Eye } from 'lucide-react'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import ChallengeQuestionRenderer from '../components/ChallengeQuestionRenderer'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import api from '../services/api'

const FocusedPracticeHomework = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { getSession, completePractice } = useFocusedPracticeStore()

  const [meta, setMeta] = useState({ name: '', targetDomain: '', challengeId: null, stageId: null })
  const [order, setOrder] = useState([]) // ordered question_ids
  const [index, setIndex] = useState(0)
  const [detail, setDetail] = useState(null) // current question (gated DTO)
  const [selected, setSelected] = useState('') // local answer before submit
  const [revealedIds, setRevealedIds] = useState(() => new Set()) // answered+revealed
  const [loading, setLoading] = useState(true)
  const [loadingQuestion, setLoadingQuestion] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const shownAt = useRef(Date.now())

  const currentId = order[index]
  const isRevealed = currentId ? revealedIds.has(currentId) : false
  const allRevealed = order.length > 0 && order.every((id) => revealedIds.has(id))

  // 1. Load session + the ordered question ids + which are already answered.
  useEffect(() => {
    let cancelled = false
    async function init() {
      setLoading(true)
      const sessionResult = await getSession(sessionId)
      if (cancelled) return
      if (!sessionResult.success) {
        toast.error(getErrorMessage(sessionResult.error))
        navigate('/focused-practice')
        return
      }
      const session = sessionResult.data
      if (!session.revealPerQuestion) {
        // Not a homework session — use the standard flow.
        navigate(`/focused-practice/${sessionId}/review`, { replace: true })
        return
      }
      const challengeId = session.challengeId
      if (!challengeId) {
        navigate(`/focused-practice/${sessionId}/review`, { replace: true })
        return
      }

      try {
        const chRes = await api.get(`/challenges/${challengeId}`)
        const stage = chRes.data?.data?.stages?.[0]
        if (!stage) throw new Error('No practice stage found')
        const ids = (stage.questions || [])
          .filter((sq) => sq.question)
          .sort((a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0))
          .map((sq) => sq.question.question_id || sq.question.questionId || sq.question.id)
          .filter(Boolean)

        // Which questions are already answered (resume support).
        let answeredIds = new Set()
        try {
          const respRes = await api.get(`/challenges/${challengeId}/stages/${stage.id}/responses`)
          for (const r of respRes.data?.data || []) {
            if (r.userAnswer !== null && r.userAnswer !== undefined && r.userAnswer !== '') {
              answeredIds.add(r.questionId)
            }
          }
        } catch {
          /* responses are best-effort for resume */
        }

        if (cancelled) return
        setMeta({
          name: session.name,
          targetDomain: session.targetDomain,
          challengeId,
          stageId: stage.id
        })
        setOrder(ids)
        setRevealedIds(answeredIds)
        // Resume at the first un-answered question; if all are answered, land on the last one so the
        // "Finish & see results" button (only shown on the last question) is reachable.
        const firstUnanswered = ids.findIndex((id) => !answeredIds.has(id))
        setIndex(firstUnanswered === -1 ? Math.max(0, ids.length - 1) : firstUnanswered)
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || 'Failed to load practice')
          navigate(`/focused-practice/${sessionId}/review`)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    init()
    return () => {
      cancelled = true
    }
  }, [sessionId, getSession, navigate])

  // 2. Fetch the current question's detail (gated: answer hidden until answered).
  const fetchDetail = useCallback(async () => {
    if (!currentId || !meta.challengeId || !meta.stageId) return
    setLoadingQuestion(true)
    try {
      const res = await api.get(
        `/challenges/${meta.challengeId}/stages/${meta.stageId}/questions/${currentId}`
      )
      setDetail(res.data?.data || null)
      setSelected(res.data?.data?.userAnswer || '')
      shownAt.current = Date.now()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load question')
    } finally {
      setLoadingQuestion(false)
    }
  }, [currentId, meta.challengeId, meta.stageId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  const handleSubmit = async () => {
    if (!selected || submitting) return
    setSubmitting(true)
    try {
      const timeSpent = Math.max(1, Math.round((Date.now() - shownAt.current) / 1000))
      await api.put(
        `/challenges/${meta.challengeId}/stages/${meta.stageId}/questions/${currentId}/response`,
        { userAnswer: selected, timeSpent }
      )
      setRevealedIds((prev) => new Set(prev).add(currentId))
      await fetchDetail() // re-fetch — now includes correct answer + rationale
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit answer')
    } finally {
      setSubmitting(false)
    }
  }

  const handleNext = () => {
    if (index < order.length - 1) setIndex((i) => i + 1)
  }

  const handleFinish = async () => {
    setFinishing(true)
    const result = await completePractice(sessionId)
    setFinishing(false)
    if (result.success) {
      navigate(`/focused-practice/${sessionId}/results`)
    } else {
      toast.error(getErrorMessage(result.error))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  const answeredCount = revealedIds.size

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Target className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {meta.name || 'Practice'}
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                Tutor homework
              </span>
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{meta.targetDomain}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {index + 1} / {order.length}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{answeredCount} answered</p>
        </div>
      </div>

      {/* Question Navigation Pills */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {order.map((id, idx) => {
            const isCurrent = idx === index
            const done = revealedIds.has(id)
            // Can only jump to answered questions or the current/next-unanswered one (sequential).
            const reachable = done || idx <= index
            return (
              <button
                key={id}
                disabled={!reachable}
                onClick={() => reachable && setIndex(idx)}
                className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                  isCurrent
                    ? 'bg-primary-600 text-white ring-2 ring-primary-300 ring-offset-1'
                    : done
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-500 border border-gray-200 disabled:opacity-50'
                }`}
              >
                {idx + 1}
              </button>
            )
          })}
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        {loadingQuestion || !detail ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <ChallengeQuestionRenderer
            question={detail}
            userAnswer={selected}
            onAnswerChange={setSelected}
            readOnly={isRevealed}
            showCorrectAnswer={isRevealed}
          />
        )}
      </div>

      {/* Actions */}
      {!isRevealed ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Eye className="h-4 w-4" /> Answer, then reveal the explanation before moving on.
          </p>
          <button
            onClick={handleSubmit}
            disabled={!selected || submitting}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Submitting…' : 'Submit & review'}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-3">
          {index < order.length - 1 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Next question
              <ChevronRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={!allRevealed || finishing}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <CheckCircle2 className="h-5 w-5" />
              {finishing ? 'Finishing…' : 'Finish & see results'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default FocusedPracticeHomework
